import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {PageRequest} from '../../shared/models/page-request.interface';
import {Observable, throwError} from 'rxjs';
import {Master} from '../../admin/models/master.interface';
import {environment} from '../../../environments/environment';
import {Page} from '../../shared/models/page.interface';
import {ApiHttpResponse} from '../../shared/models/api-http-response.interface';
import {catchError, map} from 'rxjs/operators';
import {QuestionsSearch} from '../models/questions-search.interface';
import {Questions} from '../models/questions.interface';

const INTELLIGENCE_REPORTING_API = environment.apiUrl + 'intelligencereporting';

@Injectable({
  providedIn: 'root'
})
export class QuestionsService {

  constructor(private http: HttpClient) {
  }

  static buildSearchParams(search: QuestionsSearch): string {
    let searchParams = '';
    if (search.shortCode && search.shortCode !== '') {
      searchParams += `&shortCode=${search.shortCode}`;
    }
    if (search.description && search.description !== '') {
      searchParams += `&description=${search.description}`;
    }
    if (search.category) {
      searchParams += `&category=${search.category}`;
    }
    if (search.questionType && search.questionType !== '') {
      searchParams += `&questionType=${search.questionType}`;
    }
    if (search.status && search.status !== '') {
      searchParams += `&status=${search.status}`;
    }
    if (search.level && search.level !== '') {
      searchParams += `&level=${search.level}`;
    }
    return searchParams;
  }

  getAllQuestions(search: QuestionsSearch, pageRequest: PageRequest = {page: 0, size: 100}): Observable<Page<Questions>> {
    let url = `${INTELLIGENCE_REPORTING_API}/questions?size=${pageRequest.size}&page=${pageRequest.page}`;
    const searchParams = QuestionsService.buildSearchParams(search);
    if (pageRequest.sort && pageRequest.direction) {
      url = url + `&sort=${this.getSortCode(pageRequest.sort)},${pageRequest.direction}`;
    }
    return this.http.get<Page<Questions>>(url + searchParams);
  }

  createQuestion(dto: Questions): Observable<ApiHttpResponse<Questions>> {
    return this.http.post<ApiHttpResponse<Questions>>(`${INTELLIGENCE_REPORTING_API}/questions`, dto)
      .pipe(
        catchError(err => {
          return throwError(err);
        }),
        map((res: ApiHttpResponse<Questions>) => {
          return res;
        })
      );
  }

  createQuestionCategory(description: string): Observable<ApiHttpResponse<Questions>> {
    return this.http.post<ApiHttpResponse<Questions>>(`${INTELLIGENCE_REPORTING_API}/questions-category`, description)
      .pipe(
        catchError(err => {
          return throwError(err);
        }),
        map((res: ApiHttpResponse<Questions>) => {
          return res;
        })
      );
  }

  editQuestion(dto: Questions): Observable<ApiHttpResponse<Questions>> {
    return this.http.put<ApiHttpResponse<Questions>>(`${INTELLIGENCE_REPORTING_API}/questions`, dto)
      .pipe(
        catchError(err => {
          return throwError(err);
        }),
        map((res: ApiHttpResponse<Questions>) => {
          return res;
        })
      );
  }

  updateQuestion(id: number): Observable<ApiHttpResponse<Questions>> {
    return this.http.put<ApiHttpResponse<Questions>>(`${INTELLIGENCE_REPORTING_API}/questions/update`, id)
      .pipe(
        catchError(err => {
          return throwError(err);
        }),
        map((res: ApiHttpResponse<Questions>) => {
          return res;
        })
      );
  }

  getAllQuestionCategories(search: string, pageRequest: PageRequest = {page: 0, size: 100}, categoryType?: string):
    Observable<Page<Master>> {
    let url = `${INTELLIGENCE_REPORTING_API}/questioncategories?size=${pageRequest.size}&page=${pageRequest.page}`;
    if (categoryType && categoryType !== '') {
      url = url + `&type=${categoryType}`;
    }
    if (search !== '') {
      url += '&description=' + search;
    }
    if (pageRequest.sort && pageRequest.direction) {
      url += `&sort=${this.getSortCode(pageRequest.sort)},${pageRequest.direction}`;
    }
    return this.http.get<Page<Master>>(url);
  }

  editQuestionCategory(id: number, description: string): Observable<ApiHttpResponse<Master>> {
    return this.http.put<ApiHttpResponse<Master>>(`${INTELLIGENCE_REPORTING_API}/questioncategories`, {id, description})
      .pipe(
        catchError(err => {
          return throwError(err);
        }),
        map((res: ApiHttpResponse<Master>) => {
          return res;
        })
      );
  }

  private getSortCode(sort: string): string {
    switch (sort) {
      case 'open':
        return 'isReportingAvailable';
      default:
        return sort;
    }
  }
}
